import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useHiddenCards } from "../lib/cardPreferences";
import { toTitleCase } from "../lib/formatName";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import SystematicBookView from "./SystematicBookView";
import FamilyAumPanel from "../components/FamilyAumPanel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Users,
  Search,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Download,
  IndianRupee,
  Loader2,
  CreditCard,
  UserX,
  Baby,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Check,
  Eye,
  ArrowRight,
  Wallet,
  TrendingUp,
  ArrowRightLeft,
  Banknote,
  Crown,
  X,
  Info,
  Layers,
  ArrowDownToLine,
  ArrowUpFromLine,
} from "lucide-react";
import * as XLSX from "xlsx";
import { exportStyledXlsx } from "../lib/excelExport";
import { PayeeChangesReview, SplitFoliosDownload } from "../components/commissions/PayeeChangesReview";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "₹0";
  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(2)} Cr`;
  } else if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(2)} L`;
  }
  return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

// Format full currency for table cells
const formatFullCurrency = (amount) => {
  if (!amount && amount !== 0) return "₹0.00";
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Title-casing now provided by shared `lib/formatName` (handles honorifics,
// Mc-prefixed surnames, apostrophes, &c.). Local duplicate removed.

const ClientWise = ({ restrictToTab = null, associateEditMode = "popover", hideHeader = false } = {}) => {
  const { token, isSuperAdmin } = useAuth();
  const { hidden: hiddenCards } = useHiddenCards("clientwise");
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState({});
  const [pagination, setPagination] = useState({ page: 1, limit: 100, total: 0, pages: 0 });
  
  // Filters and sorting
  const [activeTab, setActiveTab] = useState(restrictToTab || "all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("total_aum");
  const [sortOrder, setSortOrder] = useState("desc");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [associateFilter, setAssociateFilter] = useState("__all__"); // "__all__" | "_unassigned" | code
  // Open/close state for the searchable associate-filter combobox (replaces
  // the plain <Select>). Lets us close it after a user picks an entry.
  const [associateFilterOpen, setAssociateFilterOpen] = useState(false);
  const [associates, setAssociates] = useState([]);
  // Gross/Net toggle for the folio-modal CAMS/KARVY columns. Mirrors the toggle
  // inside the Reassign Folios modal — flips Commission (CAMS/KARVY) between
  // Kinntegra_Gross+Adviser_Gross and Kinntegra_Net+Adviser_Net, and recomputes
  // the yield % column.
  const [amountMode, setAmountMode] = useState("gross");
  const [assigningPan, setAssigningPan] = useState(null);
  const [openAssignPan, setOpenAssignPan] = useState(null); // which row's combobox is open
  // Family Head editing (mirrors the associate-code popover above)
  const [familyHeads, setFamilyHeads] = useState([]);
  const [assigningFamilyPan, setAssigningFamilyPan] = useState(null);
  const [openFamilyPan, setOpenFamilyPan] = useState(null);
  const [familySearch, setFamilySearch] = useState("");
  // Folio-level "From → To" editor (opens instead of the associate-code
  // popover when the parent passes `associateEditMode="folio-modal"` —
  // used by Commissions → Sub-Broker Tagging tab).
  const [folioEditClient, setFolioEditClient] = useState(null);
  // View dropdown: AUM (existing summary) | SIP | STP | SWP (book-size engine)
  const [view, setView] = useState("aum");
  // Counts for each book — shown on the view tab strip (mirrors Dashboard).
  const [bookCounts, setBookCounts] = useState({ sip: null, stp: null, swp: null });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load existing Family Heads for the assign-popover dropdown.
  const reloadFamilyHeads = useCallback(() => {
    if (!token) return;
    fetch(`${API_URL}/api/family-mapping/heads`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : { heads: [] })
      .then((d) => setFamilyHeads((d?.heads || []).slice()))
      .catch(() => setFamilyHeads([]));
  }, [token]);
  useEffect(() => { reloadFamilyHeads(); }, [reloadFamilyHeads]);

  // Load associates once (for the smart-filter dropdown + assignment dropdown).
  // Hide auto-created stubs (the bulk-import path silently creates a placeholder
  // row named `Associate {code}` whenever an unknown code shows up in the file —
  // those are usually typos that shouldn't pollute the assign dropdown). Also
  // require status=active so pending / inactive stubs stay out.
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/subbroker/list`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => {
        const rows = (d?.subbrokers || []).filter((s) => {
          if (s.status === "inactive") return false;
          if (s.auto_created && s.status !== "active") return false;
          return true;
        });
        rows.sort((a, b) => (b.mapped_clients || 0) - (a.mapped_clients || 0));
        setAssociates(rows);
      })
      .catch(() => setAssociates([]));
  }, [token]);

  // Preload SIP/STP/SWP active book counts so the view tab strip can show
  // "SIP Book (940)" style counters — matches the Dashboard tab pattern.
  useEffect(() => {
    if (!token) return;
    const ctrl = new AbortController();
    Promise.all(["SIP", "STP", "SWP"].map((t) =>
      fetch(`${API_URL}/api/clients-wise-summary/systematic?type=${t}&page=1&page_size=1&status=ACTIVE`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: ctrl.signal,
      }).then((r) => r.ok ? r.json() : null).catch(() => null)
    )).then(([sip, stp, swp]) => {
      setBookCounts({
        sip: sip?.pagination?.total ?? null,
        stp: stp?.pagination?.total ?? null,
        swp: swp?.pagination?.total ?? null,
      });
    });
    return () => ctrl.abort();
  }, [token]);

  // Fetch data
  const fetchClients = useCallback(async (page = 1) => {
    if (!token) return;
    setLoading(true);
    
    try {
      // When "_split" is selected we need the full dataset client-side (the
      // backend doesn't understand `_split`), so bump the limit to 5000 and
      // don't pass the sentinel as associate_code.
      const isSplitMode = associateFilter === "_split";
      const params = new URLSearchParams({
        has_pan: activeTab,
        sort_by: sortBy,
        sort_order: sortOrder,
        page: isSplitMode ? "1" : page.toString(),
        limit: isSplitMode ? "5000" : "100"
      });
      
      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }
      if (associateFilter && associateFilter !== "__all__" && associateFilter !== "_split") {
        params.append("associate_code", associateFilter);
      }
      
      const response = await fetch(`${API_URL}/api/clients-wise-summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Failed to fetch clients");
      
      const data = await response.json();
      setClients(data.clients || []);
      setSummary(data.summary || {});
      setPagination(data.pagination || { page: 1, limit: 100, total: 0, pages: 0 });
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Failed to load client data");
    } finally {
      setLoading(false);
    }
  }, [token, activeTab, sortBy, sortOrder, debouncedSearch, associateFilter]);

  useEffect(() => {
    fetchClients(1);
  }, [fetchClients]);

  // Handle sort
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  // Assign a client (by PAN, or by name when no PAN) to an associate code
  const handleAssignAssociate = useCallback(async (pan, code, name) => {
    if (!code || (!pan && !name)) return;
    const key = pan || name;
    setAssigningPan(key);
    try {
      const body = pan ? { pan, associate_code: code } : { name, associate_code: code };
      const resp = await fetch(`${API_URL}/api/subbroker/client-mapping/assign-single`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Assignment failed");
      }
      toast.success(`Assigned ${pan || name} → ${code}`);
      // Reload the page so the row reflects the new code + summary counts
      await fetchClients(pagination.page);
      // refresh associate counts for the dropdown
      fetch(`${API_URL}/api/subbroker/list`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((d) => {
          const rows = (d?.subbrokers || []).filter((s) => {
            if (s.status === "inactive") return false;
            if (s.auto_created && s.status !== "active") return false;
            return true;
          });
          rows.sort((a, b) => (b.mapped_clients || 0) - (a.mapped_clients || 0));
          setAssociates(rows);
        })
        .catch(() => {});
    } catch (err) {
      toast.error(err.message || "Assignment failed");
    } finally {
      setAssigningPan(null);
    }
  }, [token, fetchClients, pagination.page]);

  // Unassign a client — soft-deletes its mapping(s) so it returns to "Unassigned"
  const handleUnassignAssociate = useCallback(async (pan, name) => {
    if (!pan && !name) return;
    const key = pan || name;
    if (!window.confirm(`Remove tag from ${pan || name}? They'll move to "Unassigned only" filter.`)) return;
    setAssigningPan(key);
    try {
      const body = pan ? { pan } : { name };
      const resp = await fetch(`${API_URL}/api/subbroker/client-mapping/unassign-single`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Unassign failed");
      }
      const data = await resp.json();
      toast.success(`Unassigned ${pan || name}${data.cleared ? ` (${data.cleared} mapping${data.cleared > 1 ? "s" : ""} cleared)` : ""}`);
      await fetchClients(pagination.page);
      // refresh associate counts for the dropdown
      fetch(`${API_URL}/api/subbroker/list`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((d) => {
          const rows = (d?.subbrokers || []).filter((s) => {
            if (s.status === "inactive") return false;
            if (s.auto_created && s.status !== "active") return false;
            return true;
          });
          rows.sort((a, b) => (b.mapped_clients || 0) - (a.mapped_clients || 0));
          setAssociates(rows);
        })
        .catch(() => {});
    } catch (err) {
      toast.error(err.message || "Unassign failed");
    } finally {
      setAssigningPan(null);
    }
  }, [token, fetchClients, pagination.page]);

  // ── Family Head: assign / change ─────────────────────────────────────────
  const handleAssignFamilyHead = useCallback(async (pan, name, gh_pan, gh_name) => {
    if (!pan || !gh_pan) return;
    setAssigningFamilyPan(pan);
    try {
      const resp = await fetch(`${API_URL}/api/family-mapping/assign-single`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pan, group_head_pan: gh_pan, name, group_head_name: gh_name }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Family head assign failed");
      }
      toast.success(`Family head set: ${name || pan} → ${gh_name || gh_pan}`);
      await fetchClients(pagination.page);
      reloadFamilyHeads();
    } catch (err) {
      toast.error(err.message || "Family head assign failed");
    } finally {
      setAssigningFamilyPan(null);
    }
  }, [token, fetchClients, pagination.page, reloadFamilyHeads]);

  // ── Family Head: unassign — removes the row from family_mapping ─────────
  const handleUnassignFamilyHead = useCallback(async (pan, name) => {
    if (!pan) return;
    if (!window.confirm(`Remove family-head tag from ${name || pan}? If they are themselves a Group Head with multiple members, the whole family will be dissolved.`)) return;
    setAssigningFamilyPan(pan);
    try {
      const resp = await fetch(`${API_URL}/api/family-mapping/unassign-single`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pan }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Family unassign failed");
      }
      const data = await resp.json();
      toast.success(`Removed${data.cascade ? " (full family dissolved)" : ""}`);
      await fetchClients(pagination.page);
      reloadFamilyHeads();
    } catch (err) {
      toast.error(err.message || "Family unassign failed");
    } finally {
      setAssigningFamilyPan(null);
    }
  }, [token, fetchClients, pagination.page, reloadFamilyHeads]);

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    return sortOrder === "asc" 
      ? <ArrowUp className="h-4 w-4 ml-1 text-teal-600" />
      : <ArrowDown className="h-4 w-4 ml-1 text-teal-600" />;
  };

  // Export to Excel — fetches ALL filtered rows (not just the visible page)
  const [exporting, setExporting] = useState(false);
  const downloadPayeeChanges = async () => {
    const id = toast.loading("Preparing folio payee changes…");
    try {
      const r = await fetch(`${API_URL}/api/employees/reports/euin-moves.xlsx`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const url = URL.createObjectURL(await r.blob());
      Object.assign(document.createElement("a"), { href: url, download: "folio_payee_changes.xlsx" }).click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded folio payee changes", { id });
    } catch (e) { toast.error("Download failed", { id, description: e.message }); }
  };

  const exportToExcel = async () => {
    if (pagination.total === 0) {
      toast.error("No data to export");
      return;
    }
    setExporting(true);
    const toastId = toast.loading(`Fetching all ${pagination.total.toLocaleString()} rows…`);
    try {
      const pageLimit = 500; // max allowed by backend
      const totalPages = Math.ceil(pagination.total / pageLimit);
      const all = [];

      for (let p = 1; p <= totalPages; p += 1) {
        const params = new URLSearchParams({
          has_pan: activeTab,
          sort_by: sortBy,
          sort_order: sortOrder,
          page: String(p),
          limit: String(pageLimit),
        });
        if (debouncedSearch) params.append("search", debouncedSearch);

        const resp = await fetch(`${API_URL}/api/clients-wise-summary?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error(`Failed page ${p}`);
        const data = await resp.json();
        all.push(...(data.clients || []));
        toast.loading(`Fetching ${all.length.toLocaleString()} / ${pagination.total.toLocaleString()}…`, { id: toastId });
      }

      if (all.length === 0) {
        toast.error("No data to export", { id: toastId });
        return;
      }

      const exportData = all.map((client, idx) => ({
        "Sr No": idx + 1,
        "Name": client.name,
        "Login ID": client.login_id || client.pan || "-",
        "Associate Code": client.associate_code || "-",
        "PAN": client.pan || "-",
        "Guardian PAN": client.guardian_pan || "-",
        "Mobile": client.mobile || "-",
        "Email": client.email || "-",
        "City": client.city || "-",
        "State": client.state || "-",
        "CAMS AUM": client.cams_aum || 0,
        "KARVY AUM": client.karvy_aum || 0,
        "Total AUM": client.total_aum || 0,
        "CAMS Folios": client.cams_folios || 0,
        "KARVY Folios": client.karvy_folios || 0,
        "Total Folios": client.total_folios || 0,
      }));

      const filename = `client_wise_${activeTab}_${new Date().toISOString().split("T")[0]}.xlsx`;
      exportStyledXlsx([{ name: "Client Wise Summary", rows: exportData }], filename);
      toast.success(`Exported ${all.length.toLocaleString()} rows`, { id: toastId, description: filename });
    } catch (err) {
      console.error("Export failed:", err);
      toast.error("Export failed", { id: toastId, description: err.message });
    } finally {
      setExporting(false);
    }
  };

  // Filtered summary based on active tab
  const tabSummary = useMemo(() => {
    if (activeTab === "with_pan") {
      return {
        count: summary.with_pan_count || 0,
        label: "Clients with PAN"
      };
    } else if (activeTab === "without_pan") {
      return {
        count: summary.without_pan_count || 0,
        label: "Clients without PAN"
      };
    }
    return {
      count: summary.total_clients || 0,
      label: "All Clients"
    };
  }, [activeTab, summary]);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="client-wise-page">
      {hideHeader && associateEditMode === "folio-modal" && <div className="flex justify-end gap-2"><SplitFoliosDownload /><PayeeChangesReview /></div>}
      {/* Header */}
      {!hideHeader && (
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 flex-wrap">
            <Users className="h-6 w-6 text-teal-600" />
            Client Wise Summary
          </h1>
          <p className="text-gray-500 mt-1">
            {view === "aum"
              ? "View CAMS and KARVY portfolio totals by client"
              : `Active ${view.toUpperCase()} book size — previous calendar month basis`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {view === "aum" && (
            <Button onClick={() => fetchClients(1)} disabled={loading} variant="outline">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          )}
          {view === "aum" && (
            <Button onClick={exportToExcel} variant="outline" disabled={exporting || pagination.total === 0} data-testid="export-clientwise">
              {exporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {exporting ? "Exporting…" : "Export"}
            </Button>
          )}
          {view === "aum" && (
            <Button onClick={downloadPayeeChanges} variant="outline" data-testid="download-payee-changes">
              <Download className="h-4 w-4 mr-2" />Folio Payee Changes
            </Button>
          )}
        </div>
      </div>
      )}

      {/* View tab strip — mirrors Dashboard's tab look (icons + counts + colored active states) */}
      {!hideHeader && (
      <Tabs value={view} onValueChange={setView} className="w-full">
        <TabsList className="bg-gray-100" data-testid="clientwise-view-tabs">
          <TabsTrigger value="aum" className="data-[state=active]:bg-white" data-testid="clientwise-view-aum">
            <Wallet className="h-4 w-4 mr-2" />
            AUM
          </TabsTrigger>
          <TabsTrigger value="sip" className="data-[state=active]:bg-teal-100" data-testid="clientwise-view-sip">
            <TrendingUp className="h-4 w-4 mr-2" />
            SIP Book{bookCounts.sip !== null ? ` (${bookCounts.sip})` : ""}
          </TabsTrigger>
          <TabsTrigger value="stp" className="data-[state=active]:bg-indigo-100" data-testid="clientwise-view-stp">
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            STP Book{bookCounts.stp !== null ? ` (${bookCounts.stp})` : ""}
          </TabsTrigger>
          <TabsTrigger value="swp" className="data-[state=active]:bg-amber-100" data-testid="clientwise-view-swp">
            <Banknote className="h-4 w-4 mr-2" />
            SWP Book{bookCounts.swp !== null ? ` (${bookCounts.swp})` : ""}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      )}

      {view !== "aum" && <SystematicBookView type={view.toUpperCase()} />}

      {view === "aum" && (
        <>
          <FolioFromToDialog
            client={folioEditClient}
            associates={associates}
            token={token}
            onClose={() => setFolioEditClient(null)}
            onSaved={() => fetchClients(pagination.page)}
          />

      {/* Summary Cards — react to active tab + associate filter */}
      {!hideHeader && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {!hiddenCards.clients_in_view && (
        <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-teal-600 font-medium">Clients in view</p>
                <p className="text-3xl font-bold text-teal-700">{(summary.current_view?.total_clients ?? summary.total_clients)?.toLocaleString() || 0}</p>
                <p className="text-xs text-teal-500 mt-1">{activeTab === "all" ? "All tabs" : activeTab === "with_pan" ? "With PAN" : activeTab === "without_pan" ? "Without PAN" : "Minor"}{associateFilter !== "__all__" ? ` · ${associateFilter === "_unassigned" ? "Unassigned" : associateFilter === "_split" ? "Split payees" : associateFilter}` : ""}</p>
              </div>
              <Users className="h-10 w-10 text-teal-400" />
            </div>
          </CardContent>
        </Card>
        )}

        {!hiddenCards.cams_aum && (
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">CAMS AUM</p>
                <p className="text-3xl font-bold text-blue-700">{formatCurrency(summary.current_view?.total_cams_aum ?? summary.total_cams_aum)}</p>
                <p className="text-xs text-blue-500 mt-1">In current view</p>
              </div>
              <IndianRupee className="h-10 w-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        )}

        {!hiddenCards.karvy_aum && (
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">KARVY AUM</p>
                <p className="text-3xl font-bold text-purple-700">{formatCurrency(summary.current_view?.total_karvy_aum ?? summary.total_karvy_aum)}</p>
                <p className="text-xs text-purple-500 mt-1">In current view</p>
              </div>
              <IndianRupee className="h-10 w-10 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        )}

        {!hiddenCards.combined_aum && (
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Combined AUM</p>
                <p className="text-3xl font-bold text-green-700">{formatCurrency(summary.current_view?.total_combined_aum ?? summary.total_combined_aum)}</p>
                <p className="text-xs text-green-500 mt-1">CAMS + KARVY total</p>
              </div>
              <IndianRupee className="h-10 w-10 text-green-400" />
            </div>
          </CardContent>
        </Card>
        )}
      </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); }}>
        {/* Top filter bar — always at the very top of the tab so operators
            can drive search / associate / sort without hunting.  Renders
            in its own bordered card when the outer chrome is hidden
            (Sub-Broker Tagging mode) so the controls stand apart from the
            table below. */}
        {hideHeader ? (
          <div className="mb-4 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
              {/* Associate filter — searchable combobox with pinned "All" /
                  "Unassigned" / "Split" rows + the full associate list sorted
                  ascending by sub-broker code. Junk entries are filtered out. */}
              {isSuperAdmin && (
                <>
                  <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Show</span>
                  <Popover open={associateFilterOpen} onOpenChange={setAssociateFilterOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={associateFilterOpen}
                        className="w-[220px] h-8 justify-between text-xs font-normal"
                        data-testid="associate-filter"
                      >
                        {(() => {
                          if (associateFilter === "__all__") return "All Associates";
                          if (associateFilter === "_split") return "✂ Split payees only";
                          const a = associates.find(x => x.sub_broker_code === associateFilter);
                          return a ? (
                            <span className="truncate"><span className="font-mono">{a.sub_broker_code}</span> <span className="text-gray-500">· {toTitleCase(a.sub_broker_name)}</span></span>
                          ) : associateFilter;
                        })()}
                        <ArrowDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[320px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search by code or name…" className="h-9" />
                        <CommandList className="max-h-80">
                          <CommandEmpty>No sub-broker found.</CommandEmpty>
                          <CommandGroup heading="Quick filters">
                            {[
                              { v: "__all__", label: "All Associates" },
                              { v: "_split", label: "✂ Split payees only" },
                            ].map((o) => (
                              <CommandItem
                                key={o.v}
                                value={o.label}
                                onSelect={() => { setAssociateFilter(o.v); setAssociateFilterOpen(false); }}
                                className={associateFilter === o.v ? "bg-teal-50 text-teal-700 font-semibold" : ""}
                              >
                                {o.label}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                          {associates.length > 0 && (
                            <CommandGroup heading="Associates">
                              {[...associates]
                                .filter(a => a.sub_broker_code
                                  && String(a.sub_broker_code).toLowerCase() !== "junk"
                                  && String(a.sub_broker_name || "").toLowerCase() !== "junk")
                                .sort((a, b) => String(a.sub_broker_code).localeCompare(
                                  String(b.sub_broker_code), undefined,
                                  { numeric: true, sensitivity: "base" }))
                                .map((a) => (
                                  <CommandItem
                                    key={a.id}
                                    value={`${a.sub_broker_code} ${a.sub_broker_name || ""}`}
                                    onSelect={() => { setAssociateFilter(a.sub_broker_code); setAssociateFilterOpen(false); }}
                                    className={associateFilter === a.sub_broker_code ? "bg-teal-50 text-teal-700 font-semibold" : ""}
                                  >
                                    <span className="font-mono">{a.sub_broker_code}</span>
                                    <span className="ml-2 text-gray-500 text-xs truncate">{toTitleCase(a.sub_broker_name)}</span>
                                    <span className="ml-auto text-[10px] text-gray-400">· {a.mapped_clients || 0}</span>
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </>
              )}

              {/* Search — mirrors the modal's quick-search box */}
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                <Input
                  placeholder="Search client name or PAN…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-7 h-8 w-64 text-xs"
                  data-testid="client-search"
                />
              </div>

              {/* Sort — field dropdown + direction toggle, same layout as modal */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Sort</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[160px] h-8 text-xs">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="total_aum">Total AUM</SelectItem>
                    <SelectItem value="cams_aum">CAMS AUM</SelectItem>
                    <SelectItem value="karvy_aum">KARVY AUM</SelectItem>
                    <SelectItem value="name">Name</SelectItem>
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                  className="text-xs h-8 px-2 border border-gray-300 rounded-md bg-white hover:bg-gray-100 font-mono"
                  title={sortOrder === "asc" ? "Ascending — click to flip" : "Descending — click to flip"}
                >
                  {sortOrder === "asc" ? "▲ Asc" : "▼ Desc"}
                </button>
              </div>

              {/* Gross / Net toggle — matches modal pill style */}
              {associateEditMode === "folio-modal" && (
                <div
                  className="inline-flex items-center rounded-full border border-gray-300 bg-white text-xs font-semibold overflow-hidden"
                  role="group"
                  data-testid="clientwise-amount-mode"
                >
                  <button
                    type="button"
                    onClick={() => setAmountMode("gross")}
                    className={`px-3 py-1.5 transition ${amountMode === "gross" ? "bg-teal-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
                    data-testid="clientwise-amount-gross"
                    title="Kinntegra_Gross + Adviser_Gross (pre-GST) per RTA."
                  >
                    Gross
                  </button>
                  <button
                    type="button"
                    onClick={() => setAmountMode("net")}
                    className={`px-3 py-1.5 transition ${amountMode === "net" ? "bg-teal-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
                    data-testid="clientwise-amount-net"
                    title="Kinntegra_Net + Adviser_Net (post-GST) per RTA."
                  >
                    Net
                  </button>
                </div>
              )}

              <span className="ml-auto text-xs text-gray-500">
                {(() => {
                  if (associateFilter === "_split") {
                    const n = (clients || []).filter(c => Number(c.payee_split_count || 0) > 1).length;
                    return `${n.toLocaleString()} split client${n === 1 ? "" : "s"}`;
                  }
                  return `${pagination.total.toLocaleString()} client${pagination.total === 1 ? "" : "s"}`;
                })()}
              </span>
            </div>
          </div>
        ) : null}

        <div className="flex items-center justify-between">
          {!hideHeader && (
          <TabsList>
            {(!restrictToTab || restrictToTab === "with_pan") && (
            <TabsTrigger value="with_pan" className="flex items-center gap-2" data-testid="tab-with-pan">
              <CreditCard className="h-4 w-4" />
              With PAN
              <Badge variant="secondary" className="ml-1">{summary.with_pan_count || 0}</Badge>
            </TabsTrigger>
            )}
            {(!restrictToTab || restrictToTab === "without_pan") && (
            <TabsTrigger value="without_pan" className="flex items-center gap-2" data-testid="tab-without-pan">
              <UserX className="h-4 w-4" />
              Without PAN
              <Badge variant="secondary" className="ml-1">{summary.without_pan_count || 0}</Badge>
            </TabsTrigger>
            )}
            {(!restrictToTab || restrictToTab === "minor") && (
            <TabsTrigger value="minor" className="flex items-center gap-2" data-testid="tab-minor">
              <Baby className="h-4 w-4" />
              Minor
              <Badge variant="secondary" className="ml-1">{summary.minor_count || 0}</Badge>
            </TabsTrigger>
            )}
            {(!restrictToTab || restrictToTab === "all") && (
            <TabsTrigger value="all" className="flex items-center gap-2" data-testid="tab-all">
              <Users className="h-4 w-4" />
              All Clients
              <Badge variant="secondary" className="ml-1">{summary.total_clients || 0}</Badge>
            </TabsTrigger>
            )}
            {(!restrictToTab || restrictToTab === "family_aum") && (
            <TabsTrigger value="family_aum" className="flex items-center gap-2" data-testid="tab-family-aum">
              <Crown className="h-4 w-4" />
              Family AUM
            </TabsTrigger>
            )}
          </TabsList>
          )}

          {/* Search and Sort (inline — only used when the top filter bar
              is not rendered above, i.e. on the standalone Client-Wise
              Summary page). */}
          {!hideHeader && (
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name or PAN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-[300px]"
                data-testid="client-search"
              />
            </div>
            {isSuperAdmin && (
            <Select value={associateFilter} onValueChange={setAssociateFilter}>
              <SelectTrigger className="w-[230px]" data-testid="associate-filter">
                <SelectValue placeholder="Filter by associate" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="__all__">All Associates</SelectItem>
                <SelectItem value="_unassigned">⚠ Unassigned only</SelectItem>
                {associates.length > 0 && (
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-gray-400">Associates</div>
                )}
                {associates.map((a) => (
                  <SelectItem key={a.id} value={a.sub_broker_code}>
                    <span className="font-mono">{a.sub_broker_code}</span>
                    <span className="ml-2 text-gray-500 text-xs">{toTitleCase(a.sub_broker_name)}</span>
                    <span className="ml-2 text-[10px] text-gray-400">· {a.mapped_clients || 0}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            )}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="total_aum">Total AUM</SelectItem>
                <SelectItem value="cams_aum">CAMS AUM</SelectItem>
                <SelectItem value="karvy_aum">KARVY AUM</SelectItem>
                <SelectItem value="name">Name</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              title={sortOrder === "asc" ? "Ascending" : "Descending"}
            >
              {sortOrder === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
            </Button>
            {/* Gross / Net toggle — only in folio-modal mode; flips the CAMS
                & KARVY Commission and Yield columns between the gross and net
                basis so operators can QA both views without reloading. */}
            {associateEditMode === "folio-modal" && (
              <div
                className="inline-flex items-center rounded-full border border-gray-300 bg-white text-xs font-semibold overflow-hidden ml-1"
                role="group"
                data-testid="clientwise-amount-mode"
              >
                <button
                  type="button"
                  onClick={() => setAmountMode("gross")}
                  className={`px-3 py-1.5 transition ${amountMode === "gross" ? "bg-teal-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
                  data-testid="clientwise-amount-gross"
                  title="Kinntegra_Gross + Adviser_Gross (pre-GST) per RTA."
                >
                  Gross
                </button>
                <button
                  type="button"
                  onClick={() => setAmountMode("net")}
                  className={`px-3 py-1.5 transition ${amountMode === "net" ? "bg-teal-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
                  data-testid="clientwise-amount-net"
                  title="Kinntegra_Net + Adviser_Net (post-GST) per RTA."
                >
                  Net
                </button>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Content for the 4 client-list tabs is the same table. The Family AUM tab
            renders a different panel via the FamilyAumPanel component below. */}
        {activeTab !== "family_aum" && (
        <TabsContent value={activeTab} className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center justify-between">
                <span>{tabSummary.label} ({(associateFilter === "_split" ? (clients || []).filter(c => Number(c.payee_split_count || 0) > 1).length : pagination.total).toLocaleString()})</span>
                {loading && <Loader2 className="h-5 w-5 animate-spin text-teal-600" />}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading && clients.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
                  <span className="ml-3 text-gray-500">Loading clients...</span>
                </div>
              ) : clients.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No clients found
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className={`bg-gray-50 ${associateEditMode === "folio-modal" ? "border-b-0" : ""}`}>
                          <TableHead
                            className="w-[50px] text-center"
                            rowSpan={associateEditMode === "folio-modal" ? 2 : 1}
                          >#</TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-100 w-[28%]"
                            onClick={() => handleSort("name")}
                            rowSpan={associateEditMode === "folio-modal" ? 2 : 1}
                          >
                            <div className="flex items-center">
                              Client Name
                              <SortIcon field="name" />
                            </div>
                          </TableHead>
                          {associateEditMode !== "folio-modal" && (
                            <TableHead className="w-[14%]">Login ID</TableHead>
                          )}
                          {associateEditMode === "folio-modal" ? (
                            null
                          ) : (
                            <>
                              <TableHead className="w-[12%]">Associate Code</TableHead>
                              <TableHead className="w-[18%]">Family Head</TableHead>
                            </>
                          )}
                          {associateEditMode === "folio-modal" ? (
                            <>
                              <TableHead
                                className="cursor-pointer hover:bg-gray-100 text-right bg-blue-50 text-blue-700 border-l w-[9%]"
                                onClick={() => handleSort("cams_aum")}
                                rowSpan={2}
                                title="Weighted-average monthly AUM from WBR77 (latest month) across all folios."
                              >
                                <div className="flex items-center justify-end">
                                  CAMS AAUM
                                  <SortIcon field="cams_aum" />
                                </div>
                              </TableHead>
                              <TableHead
                                className="cursor-pointer hover:bg-gray-100 text-right bg-purple-50 text-purple-700 border-l w-[9%]"
                                onClick={() => handleSort("karvy_aum")}
                                rowSpan={2}
                                title="Average monthly AUM from MFSD205 (latest month) across all folios."
                              >
                                <div className="flex items-center justify-end">
                                  KARVY AAUM
                                  <SortIcon field="karvy_aum" />
                                </div>
                              </TableHead>
                              <TableHead colSpan={2} className="text-center bg-teal-100 text-teal-700 border-l border-b-0 font-semibold">KINNTEGRA</TableHead>
                              <TableHead colSpan={2} className="text-center bg-amber-100 text-amber-700 border-l border-b-0 font-semibold">ASSOCIATE</TableHead>
                              <TableHead colSpan={2} className="text-center bg-emerald-100 text-emerald-700 border-l border-b-0 font-semibold">TOTAL</TableHead>
                            </>
                          ) : (
                            <>
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-100 text-right w-[10%]"
                            onClick={() => handleSort("cams_aum")}
                          >
                            <div className="flex items-center justify-end">
                              {associateEditMode === "folio-modal" ? "CAMS AAUM" : "CAMS AUM"}
                              <SortIcon field="cams_aum" />
                            </div>
                          </TableHead>
                          {associateEditMode === "folio-modal" && (
                            <TableHead className="text-right w-[9%]">
                              <div className="flex items-center justify-end text-blue-700" title="Sum of CAMS Adviser_Net across every month the folio has appeared on WBR77.">
                                CAMS Comm
                              </div>
                            </TableHead>
                          )}
                          <TableHead
                            className="cursor-pointer hover:bg-gray-100 text-right w-[10%]"
                            onClick={() => handleSort("karvy_aum")}
                          >
                            <div className="flex items-center justify-end">
                              {associateEditMode === "folio-modal" ? "KARVY AAUM" : "KARVY AUM"}
                              <SortIcon field="karvy_aum" />
                            </div>
                          </TableHead>
                          {associateEditMode === "folio-modal" && (
                            <TableHead className="text-right w-[9%]">
                              <div className="flex items-center justify-end text-purple-700" title="Sum of Karvy Adviser_Net across every month the folio has appeared on MFSD205.">
                                KARVY Comm
                              </div>
                            </TableHead>
                          )}
                            </>
                          )}
                          <TableHead 
                            className={`cursor-pointer hover:bg-gray-100 text-right w-[10%] ${associateEditMode === "folio-modal" ? "bg-slate-100 border-l text-slate-700" : "bg-green-50"}`}
                            onClick={() => handleSort("total_aum")}
                            rowSpan={associateEditMode === "folio-modal" ? 2 : 1}
                          >
                            <div className="flex items-center justify-end">
                              {associateEditMode === "folio-modal" ? "Total AAUM" : "Total AUM"}
                              <SortIcon field="total_aum" />
                            </div>
                          </TableHead>
                          {associateEditMode === "folio-modal" && (
                            <TableHead className="w-[40px] text-center" rowSpan={2} />
                          )}
                        </TableRow>
                        {associateEditMode === "folio-modal" && (
                          <TableRow className="bg-gray-50">
                            {/* Sub-headers for the Kinntegra / Associate / Total
                                groups. rowSpan=2 cells in Row 1 (#, Client Name,
                                Payee, CAMS AAUM, KARVY AAUM, Total AAUM, Edit)
                                skip these positions automatically. */}
                            <TableHead
                              className="text-right bg-teal-50 text-teal-700 text-xs font-medium border-l"
                              title={amountMode === "net"
                                ? "Kinntegra net commission across CAMS + Karvy (Kinntegra_Net)."
                                : "Kinntegra gross commission across CAMS + Karvy (Kinntegra_Gross)."}
                            >
                              <div className="flex items-center justify-end">{amountMode === "net" ? "Net Amount" : "Gross Amount"}</div>
                            </TableHead>
                            <TableHead
                              className="text-right bg-teal-50 text-teal-700 text-xs font-medium"
                              title="Kinntegra commission ÷ (CAMS AAUM + Karvy AAUM), annualised × 12."
                            >
                              <div className="flex items-center justify-end">Yield %</div>
                            </TableHead>
                            <TableHead
                              className="text-right bg-amber-50 text-amber-700 text-xs font-medium border-l"
                              title={amountMode === "net"
                                ? "Associate (sub-broker) net commission across CAMS + Karvy (Adviser_Net)."
                                : "Associate (sub-broker) gross commission across CAMS + Karvy (Adviser_Gross)."}
                            >
                              <div className="flex items-center justify-end">{amountMode === "net" ? "Net Amount" : "Gross Amount"}</div>
                            </TableHead>
                            <TableHead
                              className="text-right bg-amber-50 text-amber-700 text-xs font-medium"
                              title="Associate commission ÷ (CAMS AAUM + Karvy AAUM), annualised × 12."
                            >
                              <div className="flex items-center justify-end">Yield %</div>
                            </TableHead>
                            <TableHead
                              className="text-right bg-emerald-50 text-emerald-700 text-xs font-medium border-l"
                              title={amountMode === "net"
                                ? "Total net commission (Kinntegra + Associate) across CAMS + Karvy."
                                : "Total gross commission (Kinntegra + Associate) across CAMS + Karvy."}
                            >
                              <div className="flex items-center justify-end">{amountMode === "net" ? "Net Amount" : "Gross Amount"}</div>
                            </TableHead>
                            <TableHead
                              className="text-right bg-emerald-50 text-emerald-700 text-xs font-medium"
                              title="Total commission ÷ (CAMS AAUM + Karvy AAUM), annualised × 12."
                            >
                              <div className="flex items-center justify-end">Yield %</div>
                            </TableHead>
                          </TableRow>
                        )}
                      </TableHeader>
                      <TableBody>
                        {clients
                          .filter((c) => associateFilter !== "_split" || Number(c.payee_split_count || 0) > 1)
                          .map((client, index) => (
                          <TableRow key={`${client.pan || 'no-pan'}-${client.name}-${index}`} className="hover:bg-gray-50">
                            <TableCell className="text-gray-500 font-mono text-sm text-center">
                              {(pagination.page - 1) * pagination.limit + index + 1}
                            </TableCell>
                            <TableCell className="font-medium">
                              <div 
                                className={associateEditMode === "folio-modal" ? "cursor-help inline-flex items-center gap-2" : "cursor-default"}
                                title={
                                  (associateEditMode === "folio-modal"
                                    ? `Payee: ${client.payee_split_count > 1 ? `Split across ${client.payee_split_count} sub-brokers` : (client.payee_code || "UNASSIGNED")}\n`
                                    : "") +
                                  (client.login_id ? `Login ID: ${client.login_id}\n` : '') +
                                  (client.pan ? `PAN: ${client.pan}` : 'No PAN') +
                                  (client.guardian_pan ? ` | Guardian: ${client.guardian_name || client.guardian_pan}` : '')
                                }
                              >
                                {toTitleCase(client.name)}
                                {associateEditMode === "folio-modal" && (
                                  client.payee_split_count > 1 ? (
                                    <span className="ml-1 text-[10px] uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded font-mono font-semibold">
                                      Split {client.payee_split_count}
                                    </span>
                                  ) : null
                                )}
                                {client.account_type === "minor" && (
                                  <span className="ml-2 text-xs text-orange-500 font-medium">(Minor)</span>
                                )}
                                {client.status === "TRANSFER_OUT" && (
                                  <span className="ml-2 text-[10px] uppercase tracking-wide bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded font-semibold" title="One or more folios have been transferred out (Karvy NCT-CoD / CAMS TO)">
                                    Transfer Out
                                  </span>
                                )}
                                {client.status === "TRANSMISSION" && (
                                  <span className="ml-2 text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold" title="One or more folios are in transmission to legal heir(s)">
                                    Transmission
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            {associateEditMode !== "folio-modal" && (
                              <TableCell className="font-mono text-xs text-gray-700" data-testid={`client-login-id-${index}`}>
                                {client.login_id || <span className="text-gray-300">-</span>}
                              </TableCell>
                            )}
                            {associateEditMode !== "folio-modal" && (
                            <TableCell className="font-mono text-xs" data-testid={`client-associate-code-${index}`}>
                              {associateEditMode === "folio-modal" ? (
                                client.associate_code ? (
                                  <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200 font-mono">
                                    {client.associate_code}
                                  </Badge>
                                ) : (
                                  <span className="text-gray-300 text-xs">—</span>
                                )
                              ) : (client.pan || client.name) ? (
                                <Popover
                                  open={openAssignPan === (client.pan || client.name)}
                                  onOpenChange={(o) => setOpenAssignPan(o ? (client.pan || client.name) : null)}
                                >
                                  <PopoverTrigger asChild>
                                    {client.associate_code ? (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        role="combobox"
                                        className="h-7 px-1.5 gap-1.5 hover:bg-teal-50"
                                        disabled={assigningPan === (client.pan || client.name)}
                                        data-testid={`assign-trigger-${client.pan || client.name}`}
                                        title="Click to change associate"
                                      >
                                        <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200 font-mono">
                                          {client.associate_code}
                                        </Badge>
                                        {assigningPan === (client.pan || client.name)
                                          ? <Loader2 className="h-3 w-3 animate-spin text-teal-600" />
                                          : <Pencil className="h-3 w-3 text-gray-400" />}
                                      </Button>
                                    ) : (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        role="combobox"
                                        className="h-7 w-44 justify-between text-xs font-normal"
                                        disabled={assigningPan === (client.pan || client.name)}
                                        data-testid={`assign-trigger-${client.pan || client.name}`}
                                      >
                                        {assigningPan === (client.pan || client.name) ? "Assigning…" : "Assign code…"}
                                        <ArrowUpDown className="ml-2 h-3 w-3 opacity-50" />
                                      </Button>
                                    )}
                                  </PopoverTrigger>
                                  <PopoverContent className="w-72 p-0" align="start">
                                    <Command
                                      filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}
                                    >
                                      <CommandInput placeholder="Search code or name…" className="h-9" data-testid="assign-search-input" />
                                      <CommandList>
                                        <CommandEmpty>No associate found.</CommandEmpty>
                                        {client.associate_code && (
                                          <CommandGroup heading="Actions">
                                            <CommandItem
                                              value="__unassign__"
                                              onSelect={() => {
                                                setOpenAssignPan(null);
                                                handleUnassignAssociate(client.pan, client.name);
                                              }}
                                              className="text-xs text-rose-600 data-[selected=true]:bg-rose-50"
                                              data-testid={`unassign-option-${client.pan || client.name}`}
                                            >
                                              <X className="mr-1 h-3 w-3" />
                                              <span className="font-medium">Unassign / Remove tag</span>
                                              <span className="ml-auto text-[10px] text-gray-400">{client.associate_code}</span>
                                            </CommandItem>
                                          </CommandGroup>
                                        )}
                                        <CommandGroup heading={client.associate_code ? "Reassign to" : "Assign"}>
                                          {associates.map((a) => {
                                            const isCurrent = a.sub_broker_code === client.associate_code;
                                            return (
                                              <CommandItem
                                                key={a.id}
                                                value={`${a.sub_broker_code} ${a.sub_broker_name || ""}`}
                                                onSelect={() => {
                                                  setOpenAssignPan(null);
                                                  if (a.sub_broker_code === client.associate_code) return;
                                                  handleAssignAssociate(client.pan, a.sub_broker_code, client.name);
                                                }}
                                                className={`text-xs ${isCurrent ? "bg-teal-50" : ""}`}
                                                data-testid={`assign-option-${a.sub_broker_code}`}
                                              >
                                                {isCurrent && <Check className="mr-1 h-3 w-3 text-teal-600" />}
                                                <span className="font-mono">{a.sub_broker_code}</span>
                                                <span className="ml-2 text-gray-500 truncate">{toTitleCase(a.sub_broker_name)}</span>
                                                <span className="ml-auto text-[10px] text-gray-400">{a.mapped_clients || 0}</span>
                                              </CommandItem>
                                            );
                                          })}
                                        </CommandGroup>
                                      </CommandList>
                                    </Command>
                                  </PopoverContent>
                                </Popover>
                              ) : (
                                <span className="text-gray-300 text-xs">—</span>
                              )}
                            </TableCell>
                            )}
                            {associateEditMode === "folio-modal" && (
                              null
                            )}
                            {/* Family Head — mirrors the associate column UX */}
                            {associateEditMode !== "folio-modal" && (
                            <TableCell className="font-mono text-xs" data-testid={`client-family-head-${index}`}>
                              {client.pan ? (
                                <Popover
                                  open={openFamilyPan === client.pan}
                                  onOpenChange={(o) => {
                                    setOpenFamilyPan(o ? client.pan : null);
                                    if (o) setFamilySearch("");
                                  }}
                                >
                                  <PopoverTrigger asChild>
                                    {client.group_head_pan ? (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        role="combobox"
                                        className="h-auto py-1 px-1.5 gap-1.5 hover:bg-amber-50 justify-start"
                                        disabled={assigningFamilyPan === client.pan}
                                        data-testid={`family-trigger-${client.pan}`}
                                        title={`Click to change family head\nCurrent: ${client.group_head_name || client.group_head_pan}`}
                                      >
                                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 font-normal whitespace-normal text-left leading-snug">
                                          {toTitleCase(client.group_head_name) || client.group_head_pan}
                                        </Badge>
                                        {assigningFamilyPan === client.pan
                                          ? <Loader2 className="h-3 w-3 animate-spin text-amber-600 shrink-0" />
                                          : <Pencil className="h-3 w-3 text-gray-400 shrink-0" />}
                                      </Button>
                                    ) : (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        role="combobox"
                                        className="h-7 w-40 justify-between text-xs font-normal"
                                        disabled={assigningFamilyPan === client.pan}
                                        data-testid={`family-trigger-${client.pan}`}
                                      >
                                        {assigningFamilyPan === client.pan ? "Assigning…" : "Set family head…"}
                                        <ArrowUpDown className="ml-2 h-3 w-3 opacity-50" />
                                      </Button>
                                    )}
                                  </PopoverTrigger>
                                  <PopoverContent className="w-80 p-0" align="start">
                                    <Command
                                      filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}
                                    >
                                      <CommandInput
                                        placeholder="Search head name or PAN…"
                                        className="h-9"
                                        value={familySearch}
                                        onValueChange={setFamilySearch}
                                        data-testid="family-search-input"
                                      />
                                      <CommandList>
                                        <CommandEmpty>No family head found.</CommandEmpty>
                                        <CommandGroup heading="Actions">
                                          <CommandItem
                                            value="__self__"
                                            onSelect={() => {
                                              setOpenFamilyPan(null);
                                              handleAssignFamilyHead(client.pan, client.name, client.pan, client.name);
                                            }}
                                            className="text-xs"
                                            data-testid={`family-self-${client.pan}`}
                                          >
                                            <Users className="mr-1 h-3 w-3 text-amber-600" />
                                            <span className="font-medium">Mark as own family head</span>
                                            <span className="ml-auto text-[10px] text-gray-400">{client.pan}</span>
                                          </CommandItem>
                                          {client.group_head_pan && (
                                            <CommandItem
                                              value="__unassign__"
                                              onSelect={() => {
                                                setOpenFamilyPan(null);
                                                handleUnassignFamilyHead(client.pan, client.name);
                                              }}
                                              className="text-xs text-rose-600 data-[selected=true]:bg-rose-50"
                                              data-testid={`family-unassign-${client.pan}`}
                                            >
                                              <X className="mr-1 h-3 w-3" />
                                              <span className="font-medium">Remove from family</span>
                                              <span className="ml-auto text-[10px] text-gray-400">{client.group_head_pan}</span>
                                            </CommandItem>
                                          )}
                                          {/* "Use search PAN" — type a PAN that's not yet a head */}
                                          {familySearch.length === 10 && /^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(familySearch.trim()) && (
                                            <CommandItem
                                              value="__new_head__"
                                              onSelect={() => {
                                                const newGh = familySearch.trim().toUpperCase();
                                                setOpenFamilyPan(null);
                                                handleAssignFamilyHead(client.pan, client.name, newGh, "");
                                              }}
                                              className="text-xs"
                                              data-testid={`family-new-head-${client.pan}`}
                                            >
                                              <Check className="mr-1 h-3 w-3 text-teal-600" />
                                              <span className="font-medium">Set new head → {familySearch.trim().toUpperCase()}</span>
                                            </CommandItem>
                                          )}
                                        </CommandGroup>
                                        <CommandGroup heading={client.group_head_pan ? "Switch to" : "Assign to"}>
                                          {familyHeads.map((h) => {
                                            const isCurrent = h.group_head_pan === client.group_head_pan;
                                            return (
                                              <CommandItem
                                                key={h.group_head_pan}
                                                value={`${h.group_head_pan} ${h.group_head_name || ""}`}
                                                onSelect={() => {
                                                  setOpenFamilyPan(null);
                                                  if (h.group_head_pan === client.group_head_pan) return;
                                                  handleAssignFamilyHead(client.pan, client.name, h.group_head_pan, h.group_head_name);
                                                }}
                                                className={`text-xs ${isCurrent ? "bg-amber-50" : ""}`}
                                                data-testid={`family-option-${h.group_head_pan}`}
                                              >
                                                {isCurrent && <Check className="mr-1 h-3 w-3 text-amber-600" />}
                                                <span className="font-medium truncate">{toTitleCase(h.group_head_name) || h.group_head_pan}</span>
                                                <span className="ml-2 text-[10px] font-mono text-gray-400 shrink-0">{h.group_head_pan}</span>
                                                <span className="ml-auto text-[10px] text-gray-400">{h.member_count}</span>
                                              </CommandItem>
                                            );
                                          })}
                                        </CommandGroup>
                                      </CommandList>
                                    </Command>
                                  </PopoverContent>
                                </Popover>
                              ) : (
                                <span className="text-gray-300 text-xs">—</span>
                              )}
                            </TableCell>
                            )}
                            {associateEditMode === "folio-modal" && (
                              null
                            )}
                            {/* CAMS AAUM + KARVY AAUM — standalone columns before
                                the Kinntegra / Associate / Total groups. */}
                            {associateEditMode === "folio-modal" && (
                              <>
                                <TableCell className="text-right font-mono text-sm bg-blue-50/30 border-l">
                                  {client.cams_aaum > 0 ? (
                                    <span className="text-blue-700">{formatFullCurrency(client.cams_aaum)}</span>
                                  ) : (
                                    <span className="text-gray-300">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm bg-purple-50/30 border-l">
                                  {client.karvy_aaum > 0 ? (
                                    <span className="text-purple-700">{formatFullCurrency(client.karvy_aaum)}</span>
                                  ) : (
                                    <span className="text-gray-300">-</span>
                                  )}
                                </TableCell>
                              </>
                            )}
                            {/* Kinntegra / Associate / Total group cells. Each
                                client-level amount is the sum across CAMS + Karvy
                                (per-RTA fields already come from the backend);
                                Yield % is annualised against CAMS + Karvy AAUM. */}
                            {associateEditMode === "folio-modal" && (() => {
                              const isNet = amountMode === "net";
                              const kAmt = (Number(client.cams_kinntegra_gross) || 0) + (Number(client.karvy_kinntegra_gross) || 0);
                              const aAmt = (Number(client.cams_adviser_gross) || 0)   + (Number(client.karvy_adviser_gross) || 0);
                              const kNet = (Number(client.cams_kinntegra_net) || 0)   + (Number(client.karvy_kinntegra_net) || 0);
                              const aNet = (Number(client.cams_adviser_net) || 0)     + (Number(client.karvy_adviser_net) || 0);
                              const totAaum = (Number(client.cams_aaum) || 0) + (Number(client.karvy_aaum) || 0);
                              const kShow = isNet ? kNet : kAmt;
                              const aShow = isNet ? aNet : aAmt;
                              const tShow = kShow + aShow;
                              const pct = (x) => totAaum > 0 ? (x / totAaum * 100.0 * 12.0) : 0;
                              return (
                                <>
                                  <TableCell className="text-right font-mono text-xs bg-teal-50/10 border-l" data-testid={`client-kinntegra-amt-${index}`}>
                                    {kShow > 0 ? <span className="text-teal-700 font-semibold">{formatFullCurrency(kShow)}</span> : <span className="text-gray-300">-</span>}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-xs bg-teal-50/10" data-testid={`client-kinntegra-yield-${index}`}>
                                    {pct(kShow) > 0 ? <span className="text-teal-600">{pct(kShow).toFixed(2)}%</span> : <span className="text-gray-300">-</span>}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-xs bg-amber-50/10 border-l" data-testid={`client-associate-amt-${index}`}>
                                    {aShow > 0 ? <span className="text-amber-700 font-semibold">{formatFullCurrency(aShow)}</span> : <span className="text-gray-300">-</span>}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-xs bg-amber-50/10" data-testid={`client-associate-yield-${index}`}>
                                    {pct(aShow) > 0 ? <span className="text-amber-600">{pct(aShow).toFixed(2)}%</span> : <span className="text-gray-300">-</span>}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-xs bg-emerald-50/10 border-l" data-testid={`client-total-amt-${index}`}>
                                    {tShow > 0 ? <span className="text-emerald-700 font-semibold">{formatFullCurrency(tShow)}</span> : <span className="text-gray-300">-</span>}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-xs bg-emerald-50/10" data-testid={`client-total-yield-${index}`}>
                                    {pct(tShow) > 0 ? <span className="text-emerald-600">{pct(tShow).toFixed(2)}%</span> : <span className="text-gray-300">-</span>}
                                  </TableCell>
                                </>
                              );
                            })()}
                            {associateEditMode !== "folio-modal" && (
                              <>
                            <TableCell className="text-right font-mono text-sm">
                              {client.cams_aum > 0 ? (
                                <span className="text-blue-600">{formatFullCurrency(client.cams_aum)}</span>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">
                              {client.karvy_aum > 0 ? (
                                <span className="text-purple-600">{formatFullCurrency(client.karvy_aum)}</span>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </TableCell>
                              </>
                            )}
                            <TableCell className={`text-right font-mono text-sm ${associateEditMode === "folio-modal" ? "bg-slate-50/60 border-l" : "bg-green-50 font-semibold"}`}>
                              {(() => {
                                // In folio-modal mode, Total AUM is CAMS AAUM +
                                // Karvy AAUM (commission basis). Everywhere else
                                // it stays the holding AUM from aum_master.
                                const v = associateEditMode === "folio-modal"
                                  ? ((Number(client.cams_aaum) || 0) + (Number(client.karvy_aaum) || 0))
                                  : (Number(client.total_aum) || 0);
                                return v > 0 ? (
                                  <span className={associateEditMode === "folio-modal" ? "text-slate-700" : "text-green-700"}>{formatFullCurrency(v)}</span>
                                ) : (
                                  <span className="text-gray-300">-</span>
                                );
                              })()}
                            </TableCell>
                            {associateEditMode === "folio-modal" && (
                              <TableCell className="text-center">
                                {client.pan ? (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 hover:bg-emerald-50 group"
                                    onClick={() => setFolioEditClient(client)}
                                    data-testid={`payee-edit-${client.pan}`}
                                    title="Edit folio-level sub-broker payee"
                                  >
                                    <Pencil className="h-3.5 w-3.5 text-gray-400 group-hover:text-emerald-600" />
                                  </Button>
                                ) : (
                                  <span className="text-gray-300 text-xs">—</span>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination — hidden in _split mode because we fetch the
                      full dataset (limit=5000) and filter client-side. */}
                  {pagination.pages > 1 && associateFilter !== "_split" && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <div className="text-sm text-gray-500">
                        Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total.toLocaleString()}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pagination.page <= 1}
                          onClick={() => fetchClients(pagination.page - 1)}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>
                        <span className="text-sm text-gray-600 px-2">
                          Page {pagination.page} of {pagination.pages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pagination.page >= pagination.pages}
                          onClick={() => fetchClients(pagination.page + 1)}
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {activeTab === "family_aum" && (
          <TabsContent value="family_aum" className="mt-4">
            <FamilyAumPanel token={token} />
          </TabsContent>
        )}
      </Tabs>
        </>
      )}
    </div>
  );
};

export default ClientWise;


// ───────────────────────────────────────────────────────────────────────
// FolioFromToDialog — opens when the pencil next to Associate Code is
// clicked in Sub-Broker Tagging. Fetches every folio for the client's
// PAN via /api/folio-tags/raw-by-pan (which reads MFSD205.SubBroker
// for Karvy and WBR77.AE_CODE for CAMS as the ground-truth "From"),
// and lets the operator pick a "To" sub-broker per folio. Save writes
// via POST /api/folio-tags (source="manual"), effective from the
// current month onwards.
// ───────────────────────────────────────────────────────────────────────
// Group move: pick folios (or every folio of one "From" code) and send them to one sub-broker.
function BulkMoveBar({ folios, picked, setPicked, bulkTo, setBulkTo, busy, onApply, associates, lastMove, onUndo }) {
  const fromCodes = [...new Set(folios.map((r) => r.from_code).filter(Boolean))].sort();
  const pickFrom = (code) => setPicked(new Set(folios.filter((r) => r.from_code === code).map((r) => r.folio_no)));
  return (
    <div className="flex flex-wrap items-center gap-3 border-b bg-teal-50/40 px-2 py-2 text-xs" data-testid="folio-bulk-bar">
      <span className="font-semibold uppercase tracking-wider text-teal-800">Group move</span>
      <select value="" disabled={busy} onChange={(e) => e.target.value && pickFrom(e.target.value)} className="rounded-md border border-gray-300 bg-white px-2 py-1.5" data-testid="folio-pick-from">
        <option value="">Select all folios from…</option>
        {fromCodes.map((c) => <option key={c} value={c}>{c} ({new Set(folios.filter((r) => r.from_code === c).map((r) => r.folio_no)).size} folios)</option>)}
      </select>
      <span className="text-gray-600" data-testid="folio-picked-count"><b>{picked.size}</b> folio{picked.size === 1 ? "" : "s"} selected</span>
      {picked.size > 0 && <button type="button" onClick={() => setPicked(new Set())} className="text-gray-500 underline" data-testid="folio-pick-clear">clear</button>}
      <span className="text-gray-400">→</span>
      <select value={bulkTo} onChange={(e) => setBulkTo(e.target.value)} disabled={!picked.size || busy}
        className="min-w-[260px] rounded-md border border-gray-300 bg-white px-2 py-1.5 font-mono disabled:opacity-50" data-testid="folio-bulk-to">
        <option value="">Move selected to…</option>
        {[...associates].filter((a) => a.sub_broker_code)
          .sort((a, b) => String(a.sub_broker_code).localeCompare(String(b.sub_broker_code), undefined, { numeric: true, sensitivity: "base" }))
          .map((a) => <option key={a.sub_broker_code} value={a.sub_broker_code}>{a.sub_broker_code} — {toTitleCase(a.sub_broker_name)}</option>)}
      </select>
      <button type="button" onClick={onApply} disabled={!picked.size || !bulkTo || busy}
        className="inline-flex items-center gap-1 rounded-md bg-teal-700 px-3 py-1.5 font-semibold text-white hover:bg-teal-800 disabled:opacity-40" data-testid="folio-bulk-apply">
        {busy && <Loader2 className="h-3 w-3 animate-spin" />}Move {picked.size || ""}
      </button>
      {lastMove && (
        <span className="ml-auto inline-flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-amber-800" data-testid="folio-last-move">
          Moved {lastMove.count} folio{lastMove.count === 1 ? "" : "s"} → {lastMove.to}
          <button type="button" onClick={onUndo} disabled={busy} className="rounded bg-amber-600 px-2 py-0.5 font-semibold text-white hover:bg-amber-700 disabled:opacity-40" data-testid="folio-undo-move">Undo</button>
        </span>)}
    </div>
  );
}

function FolioFromToDialog({ client, associates, token, onClose, onSaved }) {
  const [folios, setFolios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingFolio, setSavingFolio] = useState(null);
  // Filter: "aum" (default — folios with non-zero Avg Assets), "zero" (zero-AUM
  // only, e.g. redeemed/dormant folios), "all" (everything).
  const [filter, setFilter] = useState("aum");
  // Free-text search across Folio No / Scheme Name / From sub-broker code.
  const [search, setSearch] = useState("");
  // Sort state: field + direction. Default AUM (avg_assets) descending so the
  // biggest folios surface first — most useful for reassignment.
  const [sortField, setSortField] = useState("avg_assets");
  const [sortDir, setSortDir] = useState("desc");
  // Amount mode: "gross" (default) or "net" — flips Kinntegra/Associate/Total
  // columns between gross (Kinntegra_Gross/Adviser_Gross) and net (Kinntegra_Net/
  // Adviser_Net). Yield % is recomputed off the chosen amount so the two always
  // stay consistent inside the modal.
  const [amountMode, setAmountMode] = useState("gross");

  const reload = async () => {
    if (!client?.pan && !client?.guardian_pan) { setFolios([]); return; }
    setLoading(true);
    try {
      // Minors share pan with their guardian; disambiguate by sending
      // guardian_pan + name so the backend routes to the minor index.
      const params = new URLSearchParams({ pan: client.pan || "" });
      if (client.guardian_pan) {
        params.set("guardian_pan", client.guardian_pan);
        if (client.name) params.set("name", client.name);
      }
      const resp = await fetch(
        `${API_URL}/api/folio-tags/raw-by-pan?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error("Failed to load folios");
      const data = await resp.json();
      setFolios(data?.folios || []);
    } catch (e) {
      toast.error("Load failed: " + e.message);
      setFolios([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (client) reload(); }, [client]); // eslint-disable-line react-hooks/exhaustive-deps

  const [picked, setPicked] = useState(() => new Set());
  const [bulkTo, setBulkTo] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const togglePick = (f) => setPicked((s) => { const n = new Set(s); if (n.has(f)) n.delete(f); else n.add(f); return n; });
  // Move every picked folio to one sub-broker in a single go.
  const saveMany = async () => {
    const list = [...picked];
    if (!bulkTo || !list.length) return;
    if (!window.confirm(`Move ${list.length} folio${list.length === 1 ? "" : "s"} to ${bulkTo}?`)) return;
    setBulkBusy(true);
    try {
      const resp = await fetch(`${API_URL}/api/folio-tags/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ folio_nos: list, sub_broker_code: bulkTo }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Move failed");
      toast.success(`${data.moved} folio${data.moved === 1 ? "" : "s"} → ${bulkTo}`);
      setLastMove({ id: data.move_id, count: data.moved, to: bulkTo });
      setPicked(new Set()); setBulkTo("");
      await reload();
      if (onSaved) onSaved();
    } catch (e) { toast.error(e.message); } finally { setBulkBusy(false); }
  };
  const [lastMove, setLastMove] = useState(null);
  const undoMove = async () => {
    if (!lastMove) return;
    setBulkBusy(true);
    try {
      const resp = await fetch(`${API_URL}/api/folio-tags/bulk/undo/${lastMove.id}`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Undo failed");
      toast.success(`Undone — ${data.restored} folio${data.restored === 1 ? "" : "s"} back as they were`);
      setLastMove(null);
      await reload();
      if (onSaved) onSaved();
    } catch (e) { toast.error(e.message); } finally { setBulkBusy(false); }
  };

  const saveOne = async (folioNo, toCode) => {
    if (!toCode) return;
    setSavingFolio(folioNo);
    try {
      const resp = await fetch(`${API_URL}/api/folio-tags`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ folio_no: folioNo, sub_broker_code: toCode }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Save failed");
      }
      toast.success(`Folio ${folioNo} → ${toCode}`);
      await reload();
      if (onSaved) onSaved();
    } catch (e) {
      toast.error("Save failed: " + e.message);
    } finally {
      setSavingFolio(null);
    }
  };

  const sourceBadge = (src) => {
    const map = {
      karvy: "bg-orange-50 text-orange-700 border-orange-200",
      cams:  "bg-blue-50 text-blue-700 border-blue-200",
      volt:  "bg-purple-50 text-purple-700 border-purple-200",
      investor_master: "bg-slate-50 text-slate-700 border-slate-200",
      manual: "bg-emerald-50 text-emerald-700 border-emerald-200",
    };
    const label = src === "investor_master" ? "INV MST" : (src || "—").toUpperCase();
    const cls = map[src] || "bg-gray-100 text-gray-700 border-gray-200";
    return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${cls}`}>{label}</span>;
  };

  // Apply Show-filter → free-text search → sort before rendering the table.
  // Which per-row fields drive the Kinntegra / Associate / Total columns —
  // flipped by the Gross/Net toggle above the table.
  const isNet = amountMode === "net";
  const kAmt = (r) => isNet ? r.kinntegra_net : r.kinntegra_gross;
  const aAmt = (r) => isNet ? r.adviser_net   : r.adviser_gross;
  const tAmt = (r) => isNet ? r.total_net     : r.total_gross;
  const kPct = (r) => isNet ? r.kinntegra_net_yield_pct : r.kinntegra_yield_pct;
  const aPct = (r) => isNet ? r.adviser_net_yield_pct   : r.adviser_yield_pct;
  const tPct = (r) => isNet ? r.total_net_yield_pct     : r.total_yield_pct;

  // Map abstract sort field (ends with "_amount" / "_yield") to the active
  // gross/net underlying field so the toggle flips sort basis too.
  const resolvedSortField = () => {
    const suffixMap = {
      kinntegra_amount: isNet ? "kinntegra_net" : "kinntegra_gross",
      kinntegra_yield:  isNet ? "kinntegra_net_yield_pct" : "kinntegra_yield_pct",
      associate_amount: isNet ? "adviser_net" : "adviser_gross",
      associate_yield:  isNet ? "adviser_net_yield_pct" : "adviser_yield_pct",
      total_amount:     isNet ? "total_net" : "total_gross",
      total_yield:      isNet ? "total_net_yield_pct" : "total_yield_pct",
    };
    return suffixMap[sortField] || sortField;
  };

  const q = search.trim().toLowerCase();
  const filteredFolios = folios
    .filter((r) => {
      const aa = Number(r.avg_assets || 0);
      if (filter === "aum") return aa > 0;
      if (filter === "zero") return !(aa > 0);
      return true;
    })
    .filter((r) => {
      if (!q) return true;
      return (
        String(r.folio_no || "").toLowerCase().includes(q) ||
        String(r.scheme_name || "").toLowerCase().includes(q) ||
        String(r.from_code || "").toLowerCase().includes(q) ||
        String(r.from_name || "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const field = resolvedSortField();
      const av = a?.[field];
      const bv = b?.[field];
      // Numeric fields vs string fields
      if (field === "avg_assets" || field === "earning" || field === "yield_pct"
          || field.endsWith("_gross") || field.endsWith("_net")
          || field.endsWith("_yield_pct")) {
        return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
      }
      return String(av || "").localeCompare(String(bv || ""), undefined, { numeric: true, sensitivity: "base" }) * dir;
    });

  return (
    <Dialog open={!!client} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[95vw] w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-teal-600" />
            Reassign folios · {toTitleCase(client?.name || "")}
          </DialogTitle>
          <DialogDescription>
            {client?.pan && <span className="font-mono">PAN {client.pan}</span>}
            {client?.pan && " · "}
            <b>{filteredFolios.length}</b> folio{filteredFolios.length === 1 ? "" : "s"} shown · <b>From</b> reads the sub-broker code stamped on WBR77 (CAMS) or MFSD205 (Karvy) for the folio; <b>To</b> writes a manual tag that overrides it from the current month onwards.
          </DialogDescription>
        </DialogHeader>

        {/* Filter radios + quick-search + sort. AUM is the default view, Zero AUM
            surfaces redeemed/dormant folios, All shows every folio-scheme row. */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-1 py-2 border-b bg-gray-50 text-sm">
          <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Show</span>
          {[
            { v: "aum",  label: "AUM",      hint: "Non-zero Avg Asset" },
            { v: "zero", label: "Zero AUM", hint: "Avg Asset = 0" },
            { v: "all",  label: "All",      hint: "Every folio-scheme" },
          ].map((opt) => (
            <label key={opt.v} className="inline-flex items-center gap-1.5 cursor-pointer" title={opt.hint} data-testid={`folio-filter-${opt.v}`}>
              <input
                type="radio"
                name="folio-filter"
                value={opt.v}
                checked={filter === opt.v}
                onChange={() => setFilter(opt.v)}
                className="h-3.5 w-3.5 text-teal-600 border-gray-300 focus:ring-teal-500"
              />
              <span className={filter === opt.v ? "font-semibold text-teal-700" : "text-gray-700"}>{opt.label}</span>
            </label>
          ))}

          {/* Search — matches folio no, scheme name, or From sub-broker code/name */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search folio / scheme / sub-broker…"
              className="pl-7 pr-7 py-1.5 text-xs border border-gray-300 rounded-md bg-white w-64 focus:outline-none focus:ring-2 focus:ring-teal-500"
              data-testid="folio-search-input"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-5 w-5 flex items-center justify-center text-gray-400 hover:text-gray-700"
                aria-label="Clear search"
                data-testid="folio-search-clear"
              >
                ×
              </button>
            )}
          </div>

          {/* Sort — field + direction toggle */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Sort</span>
            <select
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}
              className="text-xs px-2 py-1.5 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
              data-testid="folio-sort-field"
            >
              <option value="avg_assets">AUM (Avg Asset)</option>
              <option value="kinntegra_amount">Kinntegra Amount</option>
              <option value="kinntegra_yield">Kinntegra Yield %</option>
              <option value="associate_amount">Associate Amount</option>
              <option value="associate_yield">Associate Yield %</option>
              <option value="total_amount">Total Amount</option>
              <option value="total_yield">Total Yield %</option>
              <option value="folio_no">Folio No</option>
              <option value="scheme_name">Scheme Name</option>
            </select>
            <button
              type="button"
              onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
              className="text-xs px-2 py-1.5 border border-gray-300 rounded-md bg-white hover:bg-gray-100 font-mono"
              title={sortDir === "asc" ? "Ascending — click to flip" : "Descending — click to flip"}
              data-testid="folio-sort-dir"
            >
              {sortDir === "asc" ? "▲ Asc" : "▼ Desc"}
            </button>
          </div>

          {/* Gross / Net toggle — flips the Kinntegra, Associate, and Total
              columns between Kinntegra_Gross/Adviser_Gross and
              Kinntegra_Net/Adviser_Net, with yield% recomputed off the
              chosen basis. */}
          <div
            className="inline-flex items-center rounded-full border border-gray-300 bg-white text-xs font-semibold overflow-hidden"
            role="group"
            data-testid="folio-amount-mode"
          >
            <button
              type="button"
              onClick={() => setAmountMode("gross")}
              className={`px-3 py-1.5 transition ${amountMode === "gross" ? "bg-teal-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
              data-testid="folio-amount-gross"
              title="Show Kinntegra_Gross / Adviser_Gross from WBR77 / MFSD205."
            >
              Gross
            </button>
            <button
              type="button"
              onClick={() => setAmountMode("net")}
              className={`px-3 py-1.5 transition ${amountMode === "net" ? "bg-teal-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
              data-testid="folio-amount-net"
              title="Show Kinntegra_Net / Adviser_Net (post-GST) from WBR77 / MFSD205."
            >
              Net
            </button>
          </div>

          <span className="ml-auto text-xs text-gray-500">{folios.length} total · {filteredFolios.length} match</span>
        </div>

        <BulkMoveBar folios={filteredFolios} picked={picked} setPicked={setPicked} bulkTo={bulkTo} setBulkTo={setBulkTo}
          busy={bulkBusy || loading} onApply={saveMany} associates={associates} lastMove={lastMove} onUndo={undoMove} />
        <div className="flex-1 overflow-auto border rounded-md bg-white">
          <table className="w-full text-sm table-auto">
            <thead className="bg-gray-50 border-b sticky top-0 z-10">
              {/* Three-group header: Kinntegra · Associate · Total, each with
                  Amount & Yield% sub-columns driven by the Gross/Net toggle. */}
              <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                <th className="pl-3 py-2.5" rowSpan={2}>
                  <input type="checkbox" className="accent-teal-600" data-testid="folio-pick-all"
                    checked={filteredFolios.length > 0 && filteredFolios.every((r) => picked.has(r.folio_no))}
                    onChange={(e) => setPicked((s) => { const n = new Set(s); filteredFolios.forEach((r) => (e.target.checked ? n.add(r.folio_no) : n.delete(r.folio_no))); return n; })} />
                </th>
                <th className="px-3 py-2.5 whitespace-nowrap" rowSpan={2}>Folio No</th>
                <th className="px-3 py-2.5" rowSpan={2}>Scheme</th>
                <th className="px-3 py-2.5 text-right whitespace-nowrap" rowSpan={2}>
                  <div className="inline-flex items-center justify-end gap-1">
                    <span>Avg Asset</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          onClick={(e) => e.stopPropagation()}
                          className="text-gray-400 hover:text-teal-600 focus:outline-none"
                          data-testid="aaum-info-trigger"
                          aria-label="What is Avg Asset?"
                        >
                          <Info className="h-3.5 w-3.5" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="bottom"
                        align="end"
                        className="w-80 text-xs leading-relaxed text-slate-700 normal-case tracking-normal"
                        data-testid="aaum-info-content"
                      >
                        <div className="font-semibold text-slate-900 mb-1.5">
                          Avg Asset (AAUM) — time-weighted monthly average
                        </div>
                        <div className="mb-2">
                          Formula: <span className="font-mono text-[11px]">Σ(daily AA × days) ÷ Σ(days)</span>
                        </div>
                        <div className="mb-2">
                          AAUM reflects how the balance moved <b>through</b> the
                          month, not the balance <b>on</b> any single day. For a folio
                          running STPs, source and destination AAUMs can both be
                          non-trivial even when the end-of-month snapshot is lower.
                        </div>
                        <div className="text-slate-500 italic">
                          Tip: look for the <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700 font-semibold not-italic text-[10px]"><ArrowUpFromLine className="h-2.5 w-2.5" /> STP out</span> or <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 font-semibold not-italic text-[10px]"><ArrowDownToLine className="h-2.5 w-2.5" /> STP in</span> tag next to each scheme to see which rows are mid-month movers.
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </th>
                <th className="px-4 py-2.5 text-center border-l bg-teal-50/60 text-teal-700" colSpan={2}>Kinntegra</th>
                <th className="px-4 py-2.5 text-center border-l bg-amber-50/60 text-amber-700" colSpan={2}>Associate</th>
                <th className="px-4 py-2.5 text-center border-l bg-emerald-50/60 text-emerald-700" colSpan={2}>Total</th>
                <th className="px-4 py-2.5 border-l" rowSpan={2}>From</th>
                <th className="px-4 py-2.5" rowSpan={2}>To</th>
              </tr>
              <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-t">
                <th className="px-4 py-1.5 text-right border-l bg-teal-50/40">{isNet ? "Net Amount" : "Gross Amount"}</th>
                <th className="px-4 py-1.5 text-right bg-teal-50/40">Yield %</th>
                <th className="px-4 py-1.5 text-right border-l bg-amber-50/40">{isNet ? "Net Amount" : "Gross Amount"}</th>
                <th className="px-4 py-1.5 text-right bg-amber-50/40">Yield %</th>
                <th className="px-4 py-1.5 text-right border-l bg-emerald-50/40">{isNet ? "Net Amount" : "Gross Amount"}</th>
                <th className="px-4 py-1.5 text-right bg-emerald-50/40">Yield %</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && (
                <tr><td colSpan={13} className="px-4 py-8 text-center text-gray-500">
                  <Loader2 className="inline animate-spin mr-2 h-4 w-4"/> Loading folios…
                </td></tr>
              )}
              {!loading && filteredFolios.length === 0 && (
                <tr><td colSpan={13} className="px-4 py-8 text-center text-gray-500">
                  {folios.length === 0
                    ? "No folios found for this PAN in investor_master."
                    : `No folios match the "${filter === 'aum' ? 'AUM' : filter === 'zero' ? 'Zero AUM' : 'All'}" filter.`}
                </td></tr>
              )}
              {!loading && filteredFolios.map((r, i) => (
                <tr key={`${r.folio_no}||${r.product_code || ''}||${i}`} className={picked.has(r.folio_no) ? "bg-teal-50/60" : "hover:bg-gray-50"}>
                  <td className="pl-3 py-2"><input type="checkbox" className="accent-teal-600" checked={picked.has(r.folio_no)} onChange={() => togglePick(r.folio_no)} data-testid={`folio-pick-${r.folio_no}`} /></td>
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.folio_no}</td>
                  <td className="px-3 py-2 text-xs text-gray-700">
                    <div className="inline-flex items-center gap-1.5 flex-wrap">
                      <span>{r.scheme_name || "—"}</span>
                      {r.stp_role === "source" && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700 text-[10px] font-semibold uppercase tracking-wide"
                          title="This folio also holds a non-liquid scheme — money is being STP'd OUT of this liquid fund. AAUM reflects the declining balance through the month."
                          data-testid="stp-badge-source"
                        >
                          <ArrowUpFromLine className="h-2.5 w-2.5" />
                          STP out
                        </span>
                      )}
                      {r.stp_role === "destination" && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px] font-semibold uppercase tracking-wide"
                          title="This folio also holds a liquid scheme — money is being STP'd IN to this fund. AAUM reflects the ramping balance through the month."
                          data-testid="stp-badge-destination"
                        >
                          <ArrowDownToLine className="h-2.5 w-2.5" />
                          STP in
                        </span>
                      )}
                      {Array.isArray(r.lots) && r.lots.length >= 2 && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-700 text-[10px] font-semibold uppercase tracking-wide hover:bg-amber-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400"
                              data-testid="multi-lot-badge"
                              aria-label={`${r.lots.length} lots breakdown`}
                            >
                              <Layers className="h-2.5 w-2.5" />
                              Multi-lot · {r.lots.length}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            side="top"
                            align="start"
                            className="w-80 p-0 text-xs normal-case tracking-normal"
                            data-testid="multi-lot-popover"
                          >
                            <div className="px-3 pt-2.5 pb-1.5 border-b border-slate-100">
                              <div className="font-semibold text-slate-900">Per-TRXN lot breakdown</div>
                              <div className="text-[11px] text-slate-500 mt-0.5">
                                Each lot's AAUM = Σ(AA × days) / Σ(days). Folio total = sum of all lot AAUMs.
                              </div>
                            </div>
                            <div className="max-h-64 overflow-y-auto">
                              <table className="w-full text-xs">
                                <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
                                  <tr>
                                    <th className="px-3 py-1.5 text-left">TRXN No</th>
                                    <th className="px-3 py-1.5 text-right">Days</th>
                                    <th className="px-3 py-1.5 text-right">Lot AAUM</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {r.lots.map((L, i) => (
                                    <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                                      <td className="px-3 py-1.5 font-mono text-[11px] text-slate-700">
                                        {L.trxn || "—"}
                                        <span className="ml-1 text-[9px] uppercase text-slate-400">{L.source}</span>
                                      </td>
                                      <td className="px-3 py-1.5 text-right text-slate-600">{Number(L.tdays || 0)}</td>
                                      <td className="px-3 py-1.5 text-right font-semibold text-slate-800">
                                        ₹{Number(L.lot_avg || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot className="bg-slate-50 border-t border-slate-200">
                                  <tr>
                                    <td className="px-3 py-1.5 font-semibold text-slate-700" colSpan={2}>Σ Folio AAUM</td>
                                    <td className="px-3 py-1.5 text-right font-semibold text-teal-700">
                                      ₹{r.lots.reduce((s, L) => s + Number(L.lot_avg || 0), 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-slate-700 whitespace-nowrap">
                    {r.avg_assets > 0 ? formatFullCurrency(r.avg_assets) : <span className="text-gray-300">—</span>}
                  </td>
                  {/* Kinntegra · Amount (Gross or Net per toggle) */}
                  <td className="px-4 py-2 text-right font-mono text-xs font-semibold text-teal-700 border-l bg-teal-50/20">
                    {kAmt(r) > 0 ? formatFullCurrency(kAmt(r)) : <span className="text-gray-300 font-normal">—</span>}
                  </td>
                  {/* Kinntegra · Yield % p.a. */}
                  <td className="px-4 py-2 text-right font-mono text-xs bg-teal-50/20">
                    {kPct(r) > 0 ? (
                      <span
                        className={kPct(r) >= 1 ? "text-teal-700 font-semibold" : kPct(r) >= 0.5 ? "text-amber-700" : "text-slate-500"}
                        title={r.months > 0 ? `${r.months} month${r.months === 1 ? "" : "s"} of data · annualised (× 12)` : ""}
                      >
                        {kPct(r).toFixed(2)}%
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  {/* Associate · Amount (Gross or Net per toggle) */}
                  <td className="px-4 py-2 text-right font-mono text-xs font-semibold text-amber-700 border-l bg-amber-50/20">
                    {aAmt(r) > 0 ? formatFullCurrency(aAmt(r)) : <span className="text-gray-300 font-normal">—</span>}
                  </td>
                  {/* Associate · Yield % p.a. */}
                  <td className="px-4 py-2 text-right font-mono text-xs bg-amber-50/20">
                    {aPct(r) > 0 ? (
                      <span
                        className={aPct(r) >= 1 ? "text-amber-700 font-semibold" : aPct(r) >= 0.5 ? "text-amber-600" : "text-slate-500"}
                        title={r.months > 0 ? `${r.months} month${r.months === 1 ? "" : "s"} of data · annualised (× 12)` : ""}
                      >
                        {aPct(r).toFixed(2)}%
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  {/* Total · Amount (Kinntegra + Associate — Gross or Net per toggle) */}
                  <td className="px-4 py-2 text-right font-mono text-xs font-semibold text-emerald-700 border-l bg-emerald-50/20">
                    {tAmt(r) > 0 ? formatFullCurrency(tAmt(r)) : <span className="text-gray-300 font-normal">—</span>}
                  </td>
                  {/* Total · Yield % p.a. */}
                  <td className="px-4 py-2 text-right font-mono text-xs bg-emerald-50/20">
                    {tPct(r) > 0 ? (
                      <span
                        className={tPct(r) >= 1 ? "text-emerald-700 font-semibold" : tPct(r) >= 0.5 ? "text-emerald-600" : "text-slate-500"}
                        title={r.months > 0 ? `${r.months} month${r.months === 1 ? "" : "s"} of data · annualised (× 12)` : ""}
                      >
                        {tPct(r).toFixed(2)}%
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {r.from_code ? (
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className={
                          r.override_at
                            ? "bg-amber-50 text-amber-700 border-amber-300 font-mono"
                            : "bg-teal-50 text-teal-700 border-teal-200 font-mono"
                        }>
                          {r.from_code}
                        </Badge>
                        {r.override_at && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-0.5 hover:bg-amber-100 focus:outline-none"
                                data-testid="from-override-ticker"
                                aria-label="Manual override details"
                              >
                                <Pencil className="h-2.5 w-2.5" />
                                {(() => {
                                  try {
                                    const d = new Date(r.override_at);
                                    return isNaN(d) ? "edited" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
                                  } catch { return "edited"; }
                                })()}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent side="top" align="start" className="w-64 text-xs normal-case tracking-normal p-3" data-testid="from-override-popover">
                              <div className="font-semibold text-slate-900 mb-1.5">Manual override active</div>
                              <div className="space-y-1 text-slate-700">
                                <div className="flex justify-between gap-3">
                                  <span className="text-slate-500">Was (raw):</span>
                                  <span className="font-mono text-slate-800">{r.override_from_code || "—"}</span>
                                </div>
                                <div className="flex justify-between gap-3">
                                  <span className="text-slate-500">Now:</span>
                                  <span className="font-mono font-semibold text-amber-700">{r.from_code}</span>
                                </div>
                                <div className="flex justify-between gap-3">
                                  <span className="text-slate-500">Changed on:</span>
                                  <span className="text-slate-800">
                                    {(() => {
                                      try {
                                        const d = new Date(r.override_at);
                                        return isNaN(d) ? r.override_at : d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
                                      } catch { return r.override_at; }
                                    })()}
                                  </span>
                                </div>
                                {r.override_period && (
                                  <div className="flex justify-between gap-3">
                                    <span className="text-slate-500">Effective from:</span>
                                    <span className="font-mono text-slate-800">{r.override_period}</span>
                                  </div>
                                )}
                              </div>
                              <div className="mt-2 pt-2 border-t border-slate-100 text-[11px] text-slate-500 leading-snug">
                                Next brokerage file ingestion will keep using this code for the folio — the raw stamp is intentionally overridden.
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">unassigned</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <select
                        defaultValue=""
                        disabled={savingFolio === r.folio_no}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (!v) return;
                          saveOne(r.folio_no, v);
                          e.target.value = "";
                        }}
                        className="text-xs px-2 py-1.5 border border-gray-300 rounded-md bg-white font-mono min-w-[280px] focus:outline-none focus:ring-2 focus:ring-teal-500"
                        data-testid={`folio-to-${r.folio_no}`}
                      >
                        <option value="">Choose sub-broker…</option>
                        {[...associates]
                          .filter(a => a.sub_broker_code && a.sub_broker_code !== r.from_code
                            && String(a.sub_broker_code).toLowerCase() !== "junk"
                            && String(a.sub_broker_name || "").toLowerCase() !== "junk")
                          // Ascending by sub-broker code with natural numeric
                          // ordering: 1994101 < 1994101A < 1994101B < 1994102 …
                          .sort((a, b) => String(a.sub_broker_code).localeCompare(
                            String(b.sub_broker_code), undefined,
                            { numeric: true, sensitivity: "base" }))
                          .map(a => (
                          <option key={a.sub_broker_code || a.id} value={a.sub_broker_code}>
                            {a.sub_broker_code} — {toTitleCase(a.sub_broker_name)}
                          </option>
                        ))}
                      </select>
                      {savingFolio === r.folio_no && <Loader2 className="h-3 w-3 animate-spin text-teal-600" />}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {/* Totals footer — sums each numeric column over the current
                filtered + searched set. Yield % is AUM-weighted
                (Σ amount ÷ Σ avg_assets × 100 × 12) so it stays consistent
                with the per-row yields even when some folios have no AUM. */}
            {!loading && filteredFolios.length > 0 && (() => {
              const sumAA = filteredFolios.reduce((s, r) => s + Number(r.avg_assets || 0), 0);
              const sumK  = filteredFolios.reduce((s, r) => s + Number(kAmt(r) || 0), 0);
              const sumA  = filteredFolios.reduce((s, r) => s + Number(aAmt(r) || 0), 0);
              const sumT  = filteredFolios.reduce((s, r) => s + Number(tAmt(r) || 0), 0);
              const pct = (x) => sumAA > 0 ? (x / sumAA * 100.0 * 12.0) : 0;
              return (
                <tfoot className="bg-gray-50 border-t-2 border-gray-300 sticky bottom-0 z-10">
                  <tr className="font-semibold">
                    <td className="px-3 py-2.5 text-xs uppercase tracking-wider text-gray-700" colSpan={3}>
                      Total · {filteredFolios.length} folio{filteredFolios.length === 1 ? "" : "s"}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-800 whitespace-nowrap">
                      {sumAA > 0 ? formatFullCurrency(sumAA) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-teal-800 border-l bg-teal-50/40 whitespace-nowrap">
                      {sumK > 0 ? formatFullCurrency(sumK) : <span className="text-gray-300 font-normal">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-teal-700 bg-teal-50/40">
                      {pct(sumK) > 0 ? `${pct(sumK).toFixed(2)}%` : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-amber-800 border-l bg-amber-50/40 whitespace-nowrap">
                      {sumA > 0 ? formatFullCurrency(sumA) : <span className="text-gray-300 font-normal">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-amber-700 bg-amber-50/40">
                      {pct(sumA) > 0 ? `${pct(sumA).toFixed(2)}%` : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-emerald-800 border-l bg-emerald-50/40 whitespace-nowrap">
                      {sumT > 0 ? formatFullCurrency(sumT) : <span className="text-gray-300 font-normal">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-emerald-700 bg-emerald-50/40">
                      {pct(sumT) > 0 ? `${pct(sumT).toFixed(2)}%` : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5 border-l" />
                    <td className="px-4 py-2.5" />
                  </tr>
                </tfoot>
              );
            })()}
          </table>
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t">
          <Button variant="outline" onClick={onClose} data-testid="folio-fromto-close">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
